import { DatePipe } from '@angular/common';
import { Component, OnInit, Pipe, PipeTransform, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, timeout } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { InvoiceService } from '../../services/invoice.service';
import { SharedService } from '../../services/shared.service';
import { formatOopsErrorMessage, validateDecimalInput } from '../../shared-data/helper-functions';
/** Invoice review status IDs (match backend). */
const REVIEW_STATUS = { PENDING: 1, FLAGGED: 2, APPROVED: 3 } as const;

/** InvoicePaymentStatus IDs (match dbo.InvoicePaymentStatus). */
const PAYMENT_STATUS = {
  UNPAID: 1,
  PARTIALLY_PAID: 2,
  PAID: 3,
  PARTIALLY_REFUNDED: 4,
  REFUNDED: 5
} as const;

/** One row in the Invoice and Kroll amount comparison table (matched by Rx # + fill date). */
export interface InvoiceKrollCompareRow {
  matchKey: string;
  fillDate: any;
  origRxNum: number;
  rxNum: number;
  quantity: number;
  drug: string;
  din: string;
  cost: number;
  fee: number;
  total: number;
  subPlanCode: string;
  taxable: boolean;
  invoiceTaxable: boolean | null;
  krollTaxable: boolean | null;
  taxableDiffers: boolean;
  reason: string;
  invoicePays: number | null;
  krollPays: number | null;
  /** Ontario HST (13%) on Kroll Pays when taxable; null when no Kroll Pays. */
  krollHst: number | null;
  /** Ontario HST (13%) on Invoice Pays when taxable; null when no Invoice Pays. */
  invoiceHst: number | null;
  paysDiff: number | null;
  hstDiff: number | null;
  paysMatch: boolean;
  hstMatch: boolean;
  inInvoiceOnly: boolean;
  inKrollOnly: boolean;
  costDiffers: boolean;
  feeDiffers: boolean;
  totalDiffers: boolean;
  krollCost: number | null;
  krollFee: number | null;
  krollTotal: number | null;
}

@Component({
  selector: 'app-invoice-finance-invoice-detail',
  templateUrl: './invoice-finance-invoice-detail.component.html',
  styleUrls: ['./invoice-finance-invoice-detail.component.css'],
})
export class InvoiceFinanceInvoiceDetailComponent implements OnInit {

  constructor(private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    private invoiceService: InvoiceService,
    private datepipe: DatePipe) { }

  isCreatingPurchaseLink: boolean = false;
  isForceCompletingPurchase: boolean = false;
  isRefundingPayment: boolean = false;
  isRunningScheduledCharge: boolean = false;
  forceCompleteEligible: boolean = false;
  forceCompletePaymentId: number = null;
  forceCompleteAmount: number = null;
  forceCompleteStatusLoading: boolean = false;

  @ViewChild('RemoveConfirmModalClosebutton', { static: false }) closebutton;

  validateDecimalInput: Function = validateDecimalInput;

  showErrorMessage: boolean;
  errorMessage: string;
  showWarningMessage: boolean = false;
  warningMessage: string = '';
  invoiceKroll: any = {
    invoiceItemDtos: []
  };
  invoice: any = {
    invoiceItemDtos: []
  };
  invoiceItemPlans: any = [];
  invoiceItem: any = {};
  invoiceItemPlanDtos: any = [];
  searchStartDate: any;
  searchEndDate: any;
  showLoadingMessage: boolean;
  residents: any = [];
  patientId: number;
  rxNum: any;
  invoiceItemPays: number;
  invoiceItemTotal: number;
  invoiceItemFee: number;
  invoiceItemCost: number;
  invoiceItemTaxable: boolean;
  newInvoiceItem: any = {};
  hasIaPlan: boolean;
  errorMessageAddModal: string;
  showErrorMessageAddModal: boolean = false;
  invoiceId: number;
  pharmacyId: number;
  facilityId: number;
  startDate: string;
  patientInvoiceList: any[] = [];

  ngOnInit() {
    this.sharedService.reloadUserPrivileges();
    this.route.params.subscribe(params => {
      this.invoiceId = +params['invoiceId'];

      if (isNaN(this.invoiceId) || this.invoiceId <= 0) {
        this.router.navigate(['invoice/profile']);
        return;
      }

      this.resolveReviewContextFromInvoiceId();
    });
  }

  get profileDetailsLink(): string[] {
    if (isNaN(this.pharmacyId) || isNaN(this.facilityId) || isNaN(this.patientId)) {
      return ['/invoice/profile'];
    }
    return ['/invoice/profile', String(this.pharmacyId), String(this.facilityId), String(this.patientId)];
  }

  get profileDetailsQueryParams(): { tab: string } {
    return { tab: 'invoices' };
  }

  private resolveReviewContextFromInvoiceId(): void {
    this.showLoadingMessage = true;
    this.invoiceService.getInvoiceById(this.invoiceId).subscribe(
      result => {
        if (!result || !result.success || !result.data) {
          this.showLoadingMessage = false;
          this.router.navigate(['invoice/profile']);
          return;
        }

        const inv = result.data;
        this.pharmacyId = inv.pharmacyId;
        this.facilityId = inv.facilityId;
        this.patientId = inv.patientId;
        this.startDate = this.formatReviewStartDate(inv.startDate);

        if (isNaN(this.pharmacyId)
          || isNaN(this.facilityId)
          || isNaN(this.patientId)
          || !this.startDate) {
          this.showLoadingMessage = false;
          this.router.navigate(['invoice/profile']);
          return;
        }

        this.loadPatientInvoiceNavigationList();
        this.loadFields();
      },
      () => {
        this.showLoadingMessage = false;
        this.router.navigate(['invoice/profile']);
      }
    );
  }

  private loadPatientInvoiceNavigationList(): void {
    this.patientInvoiceList = [];
    this.invoiceService.getPatientInvoiceListWithBalanceForward(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      result => {
        if (result && result.success && result.data) {
          this.patientInvoiceList = result.data as any[];
        }
      },
      () => {
        this.patientInvoiceList = [];
      }
    );
  }

  private formatReviewStartDate(startDate: any): string {
    if (!startDate) {
      return '';
    }
    const formatted = this.datepipe.transform(startDate, 'yyyy-MM-dd');
    return formatted || '';
  }

  /** Returns Swal config for status change actions. */
  private getStatusChangeConfig(status: number, isForcePending: boolean = false): any {
    const patientName = (this.invoice && this.invoice.billedTo) ? this.invoice.billedTo : 'Unknown';
    const configs = {
      1: { // Pending (Cancel Approval)
        icon: 'warning' as const,
        title: isForcePending ? 'Force Pending (Dangerous)' : 'Cancel Approval',
        html: isForcePending
          ? `<p>You are about to <strong class="text-danger">force</strong> the invoice back to <strong>Pending</strong> status.</p>
             <p>This will allow updating the Sage invoice for <strong>${patientName}</strong>.</p>
             <p class="text-danger mb-0"><strong>⚠ Use with caution:</strong> Only for Sage update workflow.</p>`
          : `<p>Are you sure you want to cancel the approval on <strong>${patientName}</strong> invoice and set it to <strong>Pending</strong>?</p>`,
        confirmButtonText: isForcePending ? 'Yes, force to Pending' : 'Yes, cancel approval',
        confirmButtonColor: isForcePending ? '#b91c1c' : '#d33'
      },
      2: { // Flagged
        icon: 'warning' as const,
        title: 'Set Invoice Status to Flagged',
        html: `<p>Are you sure you want to apply the status <strong>Flagged</strong> on <strong>${patientName}</strong> invoice?</p>`,
        confirmButtonText: 'Yes, flag it',
        confirmButtonColor: '#dc3545'
      },
      3: { // Approved
        icon: 'question' as const,
        title: 'Set Invoice Status to Approved',
        html: `<p>Are you sure you want to apply the status <strong>Approved</strong> on <strong>${patientName}</strong> invoice?</p>`,
        confirmButtonText: 'Yes, approve it',
        confirmButtonColor: '#28a745'
      }
    };
    return configs[status] || configs[1];
  }

  /** True when invoice is Approved; blocks Flag/Approve actions but allows Cancel Approval. */
  get disableStatusButton(): boolean {
    return this.invoice.invoiceReviewStatusId === REVIEW_STATUS.APPROVED;
  }

  get disableInvoiceActionsForApprovedPayment(): boolean {
    return this.paymentReviewStatusId === REVIEW_STATUS.APPROVED
      || this.hasSagePaymentRecord();
  }

  /** Privilege.InvoiceCreate — shows Force Pending (same flow as PENDING). */
  get hasInvoiceCreatePrivilege(): boolean {
    const v = this.sharedService.userPrivilegeVo;
    if (v == null) {
      return false;
    }
    return v.hasInvoiceAccessCreate === true || v.HasInvoiceAccessCreate === true;
  }

  /** Settings → User privilege — Invoice and Kroll amount comparison panel. */
  get hasSettingsUserAccess(): boolean {
    const v = this.sharedService.userPrivilegeVo;
    if (v == null) {
      return false;
    }
    return v.hasSettingsUserAccess === true || v.HasSettingsUserAccess === true;
  }

  get sentToSage(): Boolean {
    return this.invoice.invoiceSageStatusId >= 3;
  }

  /** CREATE: enabled when Approved and Sage not yet completed (< 3). */
  get disableCreateSalesInvoiceButton(): boolean {
    return !this.invoice
      || this.disableInvoiceActionsForApprovedPayment
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.invoice.invoiceSageStatusId >= 3;
  }

  /** UPDATE: enabled when Approved and Sage completed (3: Completed, 4: CompletedWithNewUser, 5: Updated). */
  get disableUpdateSalesInvoiceButton(): boolean {
    return !this.invoice
      || this.disableInvoiceActionsForApprovedPayment
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || (this.invoice.invoiceSageStatusId !== 3 && this.invoice.invoiceSageStatusId !== 4 && this.invoice.invoiceSageStatusId !== 5);
  }

  /**
   * Treat invoiceNumber as a string (coerce + trim).
   * CREATE: null/undefined, empty after trim, or exactly "0".
   * UPDATE: any other non-empty string.
   */
  private hasPersistedInvoiceId(): boolean {
    if (!this.invoice || this.invoice.invoiceNumber == null) {
      return false;
    }
    const s = String(this.invoice.invoiceNumber).trim();
    return s.length > 0 && s !== '0';
  }

  /** No invoice number yet → only Create is shown. */
  get showCreateSalesInvoiceButton(): boolean {
    return !!this.invoice && !this.hasPersistedInvoiceId();
  }

  /** Invoice number exists → only Update is shown. */
  get showUpdateSalesInvoiceButton(): boolean {
    return !!this.invoice && this.hasPersistedInvoiceId();
  }

  /**
   * Force Pending: enabled only when invoice is Approved, Sage flow completed (invoiceSageStatusId >= 3),
   * and has a valid invoice number — allows forcing back to Pending for Sage update workflow.
   */
  get disableForcePendingButton(): boolean {
    return !this.invoice
      || this.disableInvoiceActionsForApprovedPayment
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.invoice.invoiceSageStatusId < 3
      || !this.hasPersistedInvoiceId();
  }

  get paymentReviewStatusId(): number {
    if (!this.invoice || this.invoice.invoicePaymentReviewStatusId == null) {
      return REVIEW_STATUS.PENDING;
    }
    return this.invoice.invoicePaymentReviewStatusId;
  }

  /** True when payment review is Approved; blocks Flag/Approve (same as invoice review). */
  get disablePaymentReviewEditButtons(): boolean {
    return this.paymentReviewStatusId === REVIEW_STATUS.APPROVED;
  }

  get disablePaymentFlagButton(): boolean {
    return !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || this.disablePaymentReviewEditButtons
      || this.paymentReviewStatusId === REVIEW_STATUS.FLAGGED;
  }

  get disablePaymentApproveButton(): boolean {
    return !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || this.disablePaymentReviewEditButtons;
  }

  get disablePaymentPendingButton(): boolean {
    return !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || this.paymentReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.getInvoicePaymentStatusId() !== PAYMENT_STATUS.UNPAID;
  }

  /** Admin Force Pending: Approved but Purchase not available (e.g. after payment). */
  get disablePaymentForcePendingButton(): boolean {
    return !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || this.paymentReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.canPurchase;
  }

  get canPurchase(): boolean {
    if (!this.invoice || !this.hasPersistedInvoiceId()) {
      return false;
    }
    if (this.hasSagePaymentRecord()) {
      return false;
    }
    if (this.paymentReviewStatusId !== REVIEW_STATUS.APPROVED) {
      return false;
    }
    // Net Portal payment > 0 blocks another purchase; fully refunded rows do not.
    if (this.invoice.hasPortalPayment === true) {
      return false;
    }
    const paymentStatusId = this.getInvoicePaymentStatusId();
    // Unpaid / Refunded, or Paid with net 0 (stale status after full refund).
    if (paymentStatusId !== PAYMENT_STATUS.UNPAID
      && paymentStatusId !== PAYMENT_STATUS.REFUNDED
      && paymentStatusId !== PAYMENT_STATUS.PAID) {
      return false;
    }
    return true;
  }

  get disablePurchaseButton(): boolean {
    return this.isCreatingPurchaseLink || !this.canPurchase;
  }

  /** Privilege.InvoiceAdmin — Admin recovery actions on invoice detail. */
  get hasInvoiceAdminPrivilege(): boolean {
    const v = this.sharedService && this.sharedService.userPrivilegeVo;
    if (!v) {
      return false;
    }
    return v.hasInvoiceAccessAdmin === true || v.HasInvoiceAccessAdmin === true;
  }

  get disableForceCompletePaymentButton(): boolean {
    return this.isForceCompletingPurchase
      || !this.invoice
      || !this.hasPersistedInvoiceId();
  }

  get disableRunScheduledChargeButton(): boolean {
    return this.isRunningScheduledCharge
      || !this.invoice
      || !this.hasPersistedInvoiceId();
  }

  get disableAdminRefundPaymentButton(): boolean {
    return this.isRefundingPayment
      || !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || !this.canAdminRefundPayment();
  }

  /**
   * Helcim / SolvoPayment refund still needed when Portal net > 0,
   * or after Force Pending (Unpaid + payment rows, portal-local balancing only).
   */
  private canAdminRefundPayment(): boolean {
    if (!this.invoice) {
      return false;
    }
    if (this.invoice.hasPortalPayment === true) {
      return true;
    }
    const paid = this.invoice.paymentAmount != null ? Number(this.invoice.paymentAmount) : 0;
    return paid > 0.009 && this.getInvoicePaymentStatusId() === PAYMENT_STATUS.UNPAID;
  }

  get inoviceStartDate(): string {
    if (!this.invoice || !this.invoice.startDate) return '';
    return this.transformDate(this.invoice.startDate);
  }

  get inoviceEndDate(): string {
    if (!this.invoice || !this.invoice.endDate) return '';
    return this.transformDate(this.invoice.endDate);
  }

  get hasPreviousInvoice(): boolean {
    return this.getAdjacentPatientInvoice(-1) === null;
  }

  get hasNextInvoice(): boolean {
    return this.getAdjacentPatientInvoice(1) === null;
  }

  private getAdjacentPatientInvoice(direction: number): any {
    if (!this.patientInvoiceList || this.patientInvoiceList.length === 0) {
      return null;
    }

    const sorted = this.patientInvoiceList.slice().sort(function (a, b) {
      const aTime = a && a.startDate ? new Date(a.startDate).getTime() : 0;
      const bTime = b && b.startDate ? new Date(b.startDate).getTime() : 0;
      return aTime - bTime;
    });

    const currentIndex = sorted.findIndex(function (item) {
      return item && item.id === this.invoiceId;
    }, this);

    if (currentIndex < 0) {
      return null;
    }

    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= sorted.length) {
      return null;
    }

    return sorted[targetIndex];
  }

  get invoiceStatus(): string {
    if (!this.invoice || !this.invoice.invoiceReviewStatusId)
      return 'Invoice review <span class="">-</span>';

    switch (this.invoice.invoiceReviewStatusId) {
      case 1:
        return 'Invoice review <span class="text-warning">Pending</span>';
      case 2:
        return 'Invoice review <span class="text-danger">Flagged</span>';
      case 3:
        return 'Invoice review <span class="text-success">Approved</span>';
      default:
        return 'Invoice review <span class="">-</span>';
    }
    
  }

  get paymentReviewStatus(): string {
    if (!this.invoice) {
      return 'Payment review <span class="">-</span>';
    }

    switch (this.paymentReviewStatusId) {
      case 1:
        return 'Payment review <span class="text-warning">Pending</span>';
      case 2:
        return 'Payment review <span class="text-danger">Flagged</span>';
      case 3:
        return 'Payment review <span class="text-success">Approved</span>';
      default:
        return 'Payment review <span class="">-</span>';
    }
  }

  transformDate(date) {
    var dateCoverted = Date.parse(date);
    return this.datepipe.transform(dateCoverted, 'MMMM dd, yyyy');
  }

  openInvoice() {
    this.router.navigate([]).then(result => { window.open('report/invoice/' + this.invoice.id + '/' + 'Invoice', '_blank'); });
  }

  /** Shows SweetAlert2 confirmation for status change (FLAG/APPROVE/PENDING/FORCE PENDING). */
  showStatusChangeConfirm(status: number, isForcePending: boolean = false): void {
    if (this.shouldBlockStatusChange(status)) {
      return;
    }
    const config = this.getStatusChangeConfig(status, isForcePending);

    Swal.fire({
      icon: config.icon,
      title: config.title,
      html: config.html,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: config.confirmButtonText,
      confirmButtonColor: config.confirmButtonColor,
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then(result => {
      if (result.isConfirmed) {
        this.saveUpdateInvoice(status, isForcePending);
      }
    });
  }

  async saveUpdateInvoice(status: number = 0, forcePending: boolean = false): Promise<void> {
    if (this.shouldBlockStatusChange(status)) return;

    if (status > 0) {
      this.invoice.invoiceReviewStatusId = status;
    }

    this.showLoadingMessage = true;
    try {
      const result = await this.invoiceService.saveNewInvoice(
        this.invoice,
        this.sharedService.getUserId(),
        forcePending);
      if (!result.success) {
        this.loadErrorMessage(result.data);
        this.showLoadingMessage = false;
        return Promise.reject();
      }

      if (this.invoice.id === 0) {
        this.applyNewInvoiceResult(result.data);
        this.showLoadingMessage = false;
      } else {
        // Same as Refresh Page — reload invoice + Kroll compare after Invoice actions.
        this.loadFields();
      }
      return Promise.resolve();
    } catch (error) {
      this.loadErrorMessage(error.data);
      this.showLoadingMessage = false;
      return Promise.reject();
    }
  }

  /** Block Flag/Approve when already Approved; allow Cancel Approval (→ Pending). */
  private shouldBlockStatusChange(status: number): boolean {
    if (this.disableInvoiceActionsForApprovedPayment) {
      return true;
    }
    return this.disableStatusButton && status !== REVIEW_STATUS.PENDING;
  }

  private applyNewInvoiceResult(data: any): void {
    this.updateRouteValues(data.id);
    this.invoice.id = data.id;
    if (data.invoiceReviewStatusId === REVIEW_STATUS.APPROVED) {
      this.invoice = data;
    }
    this.invoiceId = data.id;
    this.loadPatientInvoiceNavigationList();
    this.loadFields();
  }

  async onSendUpdateSage(): Promise<void> {
    if (!this.disableStatusButton) return;

    if (this.invoice.id === 0) {
      this.errorMessage = "Please, save the invoice first before sending it to Sage!";
      this.showErrorMessage = true;
      window.scroll(0, 0);
      return Promise.resolve();
    }

    this.showLoadingMessage = true;
    try {
      var result = await this.invoiceService.sendUpdateSage(this.invoice.id, this.sharedService.getUserId());

      if (!result.success) {
        this.loadErrorMessage(result.data);
        this.showLoadingMessage = false;
        return Promise.resolve();
      }

      this.loadFields();
      return Promise.resolve();
    } catch (e) {
      this.loadErrorMessage(e.data);
      this.showLoadingMessage = false;
      return Promise.reject();
    }
  }

  updateRouteValues(newInvoiceId: number) {
    this.router.navigate(['/invoice/review-detail', newInvoiceId]);
  }


  loadErrorMessage(message: any) {
    console.error(message);
    this.showErrorMessage = true;
    this.errorMessage = formatOopsErrorMessage(message);
    window.scroll(0, 0);
  }

  clearErrorMessage() {
    this.showErrorMessage = false;
    this.errorMessage = "";
  }

  clearWarningMessage() {
    this.showWarningMessage = false;
    this.warningMessage = '';
  }

  validateDate(): string[] {
    var errors: string[] = [];
    if (this.getDate(this.searchEndDate) < this.getDate(this.searchStartDate))
      errors.push("The End Date must be greater then Start Date!")

    if (this.patientId == 0)
      errors.push("Please, Select the Resident first.")

    return errors;
  }

  getDate(date): Date {
    date.setHours(0, 0, 0, 0);
    return date;
  }

  reloadNavigation(invoice) {
    if (!invoice || !invoice.id) {
      return;
    }
    this.router.navigate(['invoice/review-detail', invoice.id]);
  }

  onMovePreviousInvoice() {
    const previousInvoice = this.getAdjacentPatientInvoice(-1);
    if (previousInvoice) {
      this.reloadNavigation(previousInvoice);
    }
  }

  onMoveNextInvoice() {
    const nextInvoice = this.getAdjacentPatientInvoice(1);
    if (nextInvoice) {
      this.reloadNavigation(nextInvoice);
    }
  }

  loadFields() {
    this.invoiceKroll = {
      invoiceItemDtos: []
    };
    this.invoice = {
      invoiceItemDtos: []
    };

    this.showLoadingMessage = true;
    
    const kroll$ : Observable<any> = this.invoiceService.getNewKrollInvoice(this.pharmacyId, this.facilityId, this.patientId, this.startDate).pipe(
      catchError(err => {
        this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        return of(null); 
      })
    );

    const invoice$ : Observable<any> = this.invoiceService.getInvoiceById(this.invoiceId).pipe(
      catchError(err => {
        this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        return of(null); 
      })
    );

    forkJoin([kroll$, invoice$]).subscribe(([krollResult, invoiceResult]) => {
      if (krollResult) {
        this.invoiceKroll = krollResult.data;
      }

      if (invoiceResult) {
        this.invoice = invoiceResult.data;
        if (this.invoice && this.invoice.invoicePaymentReviewStatusId == null) {
          this.invoice.invoicePaymentReviewStatusId = REVIEW_STATUS.PENDING;
        }
      }

      this.rebuildInvoiceKrollCompareRows();
      this.showLoadingMessage = false;
      this.refreshForceCompleteStatus();
    });
  } 

  public removeEprescriber() {
    this.invoice.invoiceItemDtos = this.invoice.invoiceItemDtos.filter(i => i.rxNum !== this.rxNum);

    this.closebutton.nativeElement.click();
    this.loadSummary();
    this.rebuildInvoiceKrollCompareRows();
  }

  public openDetailsModal(rxNum) {
    this.loadInvoiceItem(rxNum);
    this.invoiceItemPlanDtos = this.invoiceItem.invoiceItemPlanDtos;
  }

  private loadInvoiceItem(rxNum: any) {
    this.rxNum = rxNum;
    this.invoiceItem = this.invoice.invoiceItemDtos.find(i => i.rxNum == this.rxNum);
  }

  public openRemoveModal(rxNum) {
    this.loadInvoiceItem(rxNum);
  }

  public openEditModal(rxNum) {
    this.loadInvoiceItem(rxNum);    
    this.invoiceItemTotal = this.invoiceItem.total;
    this.invoiceItemFee = this.invoiceItem.fee;
    this.invoiceItemCost = this.invoiceItem.cost;
    this.invoiceItemPays = this.invoiceItem.pays;
    this.invoiceItemTaxable = this.invoiceItem.taxable;
  }

  public saveInvoiceItemPays() {
    this.invoiceItem.total = Number(this.invoiceItemTotal);
    this.invoiceItem.fee = Number(this.invoiceItemFee);
    this.invoiceItem.cost = Number(this.invoiceItemCost);
    this.invoiceItem.pays = Number(this.invoiceItemPays);
    this.invoiceItem.taxable = Boolean(this.invoiceItemTaxable);

    this.invoiceItem.reason = "Edited by " + this.sharedService.userPrivilegeVo.fullName;
    this.invoiceItem.reasonCode = 8;

    this.loadSummary();
    this.rebuildInvoiceKrollCompareRows();
  }


  private loadSummary() {
    this.invoice.subTotal = Number(parseFloat(this.subTotal.toString()).toFixed(2));
    this.invoice.subTotalTaxableItems = Number(parseFloat(this.subTotalTaxableItems.toString()).toFixed(2));
    this.invoice.subTotalNonTaxableItems = Number(parseFloat(this.subTotalNonTaxableItems.toString()).toFixed(2));
    this.invoice.hst = Number(parseFloat(this.hst.toString()).toFixed(2));
    this.invoice.total = Number(parseFloat(this.total.toString()).toFixed(2));
  }

  public openAddInvoiceItemModal() {
    if (this.disableStatusButton) return;
    this.newInvoiceItem = {};
  }

  public saveNewInvoiceItem() {
    if (this.disableStatusButton) return;

    if (this.ValidateFieldsNewInvoice()) {
      this.showErrorMessageAddModal = true;
      this.errorMessageAddModal = "All fields are mandatory!";
    } else {
      this.clearErrorMessageAddModal();
      this.newInvoiceItem.reason = "Edited by " + this.sharedService.userPrivilegeVo.fullName;
      this.newInvoiceItem.reasonCode = 8;
      this.invoice.invoiceItemDtos.push(this.newInvoiceItem);
      this.loadSummary();
      this.rebuildInvoiceKrollCompareRows();
      document.getElementById("closeAddModalButton").click();
    }
  }

  private ValidateFieldsNewInvoice() {
    return this.newInvoiceItem.rxNum == null ||
      this.newInvoiceItem.fillDate == null ||
      this.newInvoiceItem.quantity == null ||
      this.newInvoiceItem.drug == null ||
      this.newInvoiceItem.din == null ||
      this.newInvoiceItem.cost == null ||
      this.newInvoiceItem.fee == null ||
      this.newInvoiceItem.total == null ||
      this.newInvoiceItem.pays == null;
  }

  clearErrorMessageAddModal() {
    this.showErrorMessageAddModal = false;
    this.errorMessageAddModal = "";
  }

  get subTotal(): number {
    if (this.invoice.invoiceItemDtos) {
      return this.invoice.invoiceItemDtos.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
    } else {
      return 0;
    }
  }

  get subTotalTaxableItems(): number {
    if (this.invoice.invoiceItemDtos) {
      const taxableItems = this.invoice.invoiceItemDtos.filter(item => Boolean(item.taxable));
      return taxableItems.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
    } else {
      return 0;
    }
  }

  get subTotalNonTaxableItems(): number {
    if (this.invoice.invoiceItemDtos) {
      const nonTaxableItems = this.invoice.invoiceItemDtos.filter(item => !Boolean(item.taxable));
      return nonTaxableItems.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
    } else {
      return 0;
    }
  }

  get hst(): number {
    if (this.invoice.invoiceItemDtos && !this.hasIaPlan) {
      const taxableItems = this.invoice.invoiceItemDtos.filter(item => Boolean(item.taxable));
      const paysSum = taxableItems.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
      return Math.round(Number(paysSum) * 0.13 * 100) / 100;
    } else {
      return 0;
    }
  }

  get total(): number {
    return this.subTotal + this.hst;
  }

  onAmountBlurEdit() {
    this.invoiceItemTotal = Number(this.invoiceItemCost) + Number(this.invoiceItemFee);
  }

  onAmountBlurAdd() {
    this.newInvoiceItem.total = Number(this.newInvoiceItem.cost) + Number(this.newInvoiceItem.fee);
  }

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  sortColumnKroll: string = '';
  sortDirectionKroll: 'asc' | 'desc' = 'asc';
  sortColumnCompare: string = 'fillDate';
  sortDirectionCompare: 'asc' | 'desc' = 'asc';
  private _invoiceKrollCompareRows: InvoiceKrollCompareRow[] = [];

  get invoiceKrollCompareRows(): InvoiceKrollCompareRow[] {
    return this._invoiceKrollCompareRows;
  }

  get invoiceKrollComparePaysMismatchCount(): number {
    return this._invoiceKrollCompareRows.filter(r => !r.paysMatch).length;
  }

  get compareTotalInvoicePays(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const pays = this._invoiceKrollCompareRows[i].invoicePays;
      if (pays != null) {
        sum += Number(pays);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalKrollPays(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const pays = this._invoiceKrollCompareRows[i].krollPays;
      if (pays != null) {
        sum += Number(pays);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalKrollHst(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const hst = this._invoiceKrollCompareRows[i].krollHst;
      if (hst != null) {
        sum += Number(hst);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalInvoiceHst(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const hst = this._invoiceKrollCompareRows[i].invoiceHst;
      if (hst != null) {
        sum += Number(hst);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalPaysDiff(): number {
    return Number((this.compareTotalInvoicePays - this.compareTotalKrollPays).toFixed(2));
  }

  get compareTotalHstDiff(): number {
    return Number((this.compareTotalInvoiceHst - this.compareTotalKrollHst).toFixed(2));
  }

  get compareTotalsPaysMatch(): boolean {
    return this.compareAmountsEqual(this.compareTotalInvoicePays, this.compareTotalKrollPays);
  }

  get compareTotalsHstMatch(): boolean {
    return this.compareAmountsEqual(this.compareTotalInvoiceHst, this.compareTotalKrollHst);
  }

  /** Patient amount due from Kroll side (Pays + HST). */
  get compareTotalKrollCharge(): number {
    return Number((this.compareTotalKrollPays + this.compareTotalKrollHst).toFixed(2));
  }

  /** Patient amount due from Invoice side (Pays + HST). */
  get compareTotalInvoiceCharge(): number {
    return Number((this.compareTotalInvoicePays + this.compareTotalInvoiceHst).toFixed(2));
  }

  get compareTotalChargeDiff(): number {
    return Number((this.compareTotalInvoiceCharge - this.compareTotalKrollCharge).toFixed(2));
  }

  get compareTotalsChargeMatch(): boolean {
    return this.compareAmountsEqual(this.compareTotalInvoiceCharge, this.compareTotalKrollCharge);
  }

  /** Truncate drug name for Comparison table (max 100 characters). */
  public truncateCompareDrug(drug: string): string {
    if (!drug) {
      return '';
    }
    const text = String(drug);
    if (text.length <= 100) {
      return text;
    }
    return text.substring(0, 100) + '…';
  }

  public isNonZeroCompareDiff(diff: number | null): boolean {
    if (diff == null) {
      return false;
    }
    return !this.compareAmountsEqual(Number(diff), 0);
  }

  /** True when HST should show as a number (non-null and not zero). */
  public hasDisplayableCompareHst(hst: number | null): boolean {
    if (hst == null) {
      return false;
    }
    return !this.compareAmountsEqual(Number(hst), 0);
  }

  public sort(column: string, table: string = 'invoice') {
    let data = [];
    let currentSortColumn = '';
    let currentSortDirection = 'asc';

    if (table === 'invoice') {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
      currentSortColumn = this.sortColumn;
      currentSortDirection = this.sortDirection;
      data = this.invoice.invoiceItemDtos;
    } else if (table === 'compare') {
      if (this.sortColumnCompare === column) {
        this.sortDirectionCompare = this.sortDirectionCompare === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumnCompare = column;
        this.sortDirectionCompare = 'asc';
      }
      currentSortColumn = this.sortColumnCompare;
      currentSortDirection = this.sortDirectionCompare;
      data = this._invoiceKrollCompareRows;
    } else {
      if (this.sortColumnKroll === column) {
        this.sortDirectionKroll = this.sortDirectionKroll === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumnKroll = column;
        this.sortDirectionKroll = 'asc';
      }
      currentSortColumn = this.sortColumnKroll;
      currentSortDirection = this.sortDirectionKroll;
      data = this.invoiceKroll.invoiceItemDtos;
    }

    if (!data) return;

    data.sort((a, b) => {
      let valA = a[column];
      let valB = b[column];

      if (table === 'compare' && column === 'taxable') {
        valA = a.taxable ? 1 : 0;
        valB = b.taxable ? 1 : 0;
      } else if ((table === 'invoice' || table === 'kroll') && column === 'taxable') {
        valA = a.taxable ? 1 : 0;
        valB = b.taxable ? 1 : 0;
      } else if ((table === 'invoice' || table === 'kroll') && column === 'hst') {
        valA = this.resolveOntarioHst(a.pays != null ? Number(a.pays) : null, a.taxable != null ? Boolean(a.taxable) : null);
        valB = this.resolveOntarioHst(b.pays != null ? Number(b.pays) : null, b.taxable != null ? Boolean(b.taxable) : null);
      }

      // Handle null/undefined
      if (valA == null) valA = '';
      if (valB == null) valB = '';

      let res = 0;
      if (typeof valA === 'number' && typeof valB === 'number') {
        res = valA - valB;
      } else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        res = (valA === valB) ? 0 : (valA ? 1 : -1);
      } else {
        // Simple string comparison for everything else (including dates as strings)
        res = valA.toString().localeCompare(valB.toString(), undefined, { numeric: true, sensitivity: 'base' });
      }

      return currentSortDirection === 'asc' ? res : -res;
    });

    if (table === 'compare') {
      this._invoiceKrollCompareRows = data;
    }
  }

  private rebuildInvoiceKrollCompareRows(): void {
    if (!this.hasSettingsUserAccess) {
      this._invoiceKrollCompareRows = [];
      return;
    }
    const invoiceItems = (this.invoice && this.invoice.invoiceItemDtos) ? this.invoice.invoiceItemDtos : [];
    const krollItems = (this.invoiceKroll && this.invoiceKroll.invoiceItemDtos) ? this.invoiceKroll.invoiceItemDtos : [];
    const krollByKey: { [key: string]: any[] } = {};

    for (let i = 0; i < krollItems.length; i++) {
      const item = krollItems[i];
      const key = this.getInvoiceKrollMatchKey(item);
      if (!krollByKey[key]) {
        krollByKey[key] = [];
      }
      krollByKey[key].push(item);
    }

    const keys: { [key: string]: boolean } = {};
    const rows: InvoiceKrollCompareRow[] = [];

    for (let i = 0; i < invoiceItems.length; i++) {
      keys[this.getInvoiceKrollMatchKey(invoiceItems[i])] = true;
    }
    for (const key in krollByKey) {
      if (krollByKey.hasOwnProperty(key)) {
        keys[key] = true;
      }
    }

    for (const key in keys) {
      if (!keys.hasOwnProperty(key)) {
        continue;
      }
      const invoiceItem = invoiceItems.find(item => this.getInvoiceKrollMatchKey(item) === key);
      const krollGroup = krollByKey[key] || [];
      const krollLead = krollGroup.length ? krollGroup[0] : null;
      const source = invoiceItem || krollLead;
      if (!source) {
        continue;
      }

      const invoicePays = invoiceItem != null ? Number(invoiceItem.pays) : null;
      const krollPays = krollGroup.length ? this.resolveKrollPaysForGroup(krollGroup) : null;
      const paysMatch = invoicePays != null && krollPays != null
        && this.compareAmountsEqual(invoicePays, krollPays);

      const cost = invoiceItem != null ? Number(invoiceItem.cost) : Number(krollLead.cost);
      const fee = invoiceItem != null ? Number(invoiceItem.fee) : Number(krollLead.fee);
      const total = invoiceItem != null ? Number(invoiceItem.total) : Number(krollLead.total);
      const krollCost = krollLead != null ? Number(krollLead.cost) : null;
      const krollFee = krollLead != null ? Number(krollLead.fee) : null;
      const krollTotal = krollLead != null ? Number(krollLead.total) : null;
      const invoiceTaxable = invoiceItem != null ? Boolean(invoiceItem.taxable) : null;
      const krollTaxable = krollLead != null ? Boolean(krollLead.taxable) : null;
      const taxable = invoiceTaxable != null ? invoiceTaxable : (krollTaxable != null ? krollTaxable : false);
      const taxableDiffers = invoiceTaxable != null && krollTaxable != null && invoiceTaxable !== krollTaxable;
      const krollHst = this.resolveOntarioHst(krollPays, krollTaxable);
      const invoiceHst = this.resolveOntarioHst(invoicePays, invoiceTaxable);
      const hstMatch = invoiceHst != null && krollHst != null
        && this.compareAmountsEqual(invoiceHst, krollHst);

      rows.push({
        matchKey: key,
        fillDate: source.fillDate,
        origRxNum: source.origRxNum,
        rxNum: source.rxNum,
        quantity: source.quantity,
        drug: source.drug,
        din: source.din,
        cost: cost,
        fee: fee,
        total: total,
        subPlanCode: this.resolveKrollSubPlanCodes(krollGroup),
        taxable: taxable,
        invoiceTaxable: invoiceTaxable,
        krollTaxable: krollTaxable,
        taxableDiffers: taxableDiffers,
        reason: invoiceItem != null && invoiceItem.reason ? invoiceItem.reason : '',
        invoicePays: invoicePays,
        krollPays: krollPays,
        krollHst: krollHst,
        invoiceHst: invoiceHst,
        paysDiff: invoicePays != null && krollPays != null
          ? Number((invoicePays - krollPays).toFixed(2))
          : null,
        hstDiff: invoiceHst != null && krollHst != null
          ? Number((invoiceHst - krollHst).toFixed(2))
          : null,
        paysMatch: paysMatch,
        hstMatch: hstMatch,
        inInvoiceOnly: invoiceItem != null && krollGroup.length === 0,
        inKrollOnly: invoiceItem == null && krollGroup.length > 0,
        costDiffers: invoiceItem != null && krollLead != null && !this.compareAmountsEqual(cost, krollCost),
        feeDiffers: invoiceItem != null && krollLead != null && !this.compareAmountsEqual(fee, krollFee),
        totalDiffers: invoiceItem != null && krollLead != null && !this.compareAmountsEqual(total, krollTotal),
        krollCost: krollCost,
        krollFee: krollFee,
        krollTotal: krollTotal,
      });
    }

    rows.sort((a, b) => {
      const dateA = this.formatFillDateKey(a.fillDate);
      const dateB = this.formatFillDateKey(b.fillDate);
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      return Number(a.rxNum) - Number(b.rxNum);
    });

    this._invoiceKrollCompareRows = rows;
  }

  private getInvoiceKrollMatchKey(item: any): string {
    return String(item.rxNum) + '|' + this.formatFillDateKey(item.fillDate);
  }

  private formatFillDateKey(fillDate: any): string {
    if (!fillDate) {
      return '';
    }
    const formatted = this.datepipe.transform(fillDate, 'yyyy-MM-dd');
    return formatted ? formatted : String(fillDate);
  }

  private compareAmountsEqual(a: number, b: number): boolean {
    return Math.abs(Number(a) - Number(b)) < 0.005;
  }

  /**
   * Ontario HST on patient Pays: taxable amount × 13%, rounded to 2 decimals
   * (same approach as invoice HST / InvoiceDto.Hst).
   */
  public resolveOntarioHst(pays: number | null, taxable: boolean | null): number | null {
    if (pays == null) {
      return null;
    }
    if (taxable === false) {
      return 0;
    }
    return Math.round(Number(pays) * 0.13 * 100) / 100;
  }

  /** Sum patient Pays on Kroll lines for the same Rx (Cash plan lines when present). */
  private resolveKrollPaysForGroup(krollItems: any[]): number {
    if (!krollItems || !krollItems.length) {
      return 0;
    }
    const cashLines = krollItems.filter(item =>
      item.subPlanCode && String(item.subPlanCode).indexOf('Cash') >= 0);
    const lines = cashLines.length ? cashLines : krollItems;
    let sum = 0;
    for (let i = 0; i < lines.length; i++) {
      sum += Number(lines[i].pays || 0);
    }
    return Number(sum.toFixed(2));
  }

  private resolveKrollSubPlanCodes(krollItems: any[]): string {
    if (!krollItems || !krollItems.length) {
      return '';
    }
    const unique: string[] = [];
    for (let i = 0; i < krollItems.length; i++) {
      const code = krollItems[i].subPlanCode;
      if (code && unique.indexOf(code) < 0) {
        unique.push(code);
      }
    }
    return unique.join(' / ');
  }

  createSalesInvoice(): void {
    if (!this.invoice || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED) return;
    if (this.showLoadingMessage) return;

    this.ensureSageCustomerProfileThen(() => this.confirmSalesInvoiceFlow('create'));
  }

  updateSalesInvoice(): void {
    if (!this.invoice || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED) return;
    if (this.showLoadingMessage) return;
    if (!this.hasPersistedInvoiceId()) {
      Swal.fire({
        icon: 'warning',
        title: 'Invoice number required',
        text: 'Cannot update Sage invoice without an invoice number.',
      });
      return;
    }

    this.ensureSageCustomerProfileThen(() => this.confirmSalesInvoiceFlow('update'));
  }

  /** Loads Sage profile; runs callback only when Sage customer id is present. */
  private ensureSageCustomerProfileThen(onReady: () => void): void {
    this.invoiceService.getInvoiceSageProfile(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      res => {
        const profile = res && res.data;
        if (profile == null || profile.sageCustomerId == null || profile.sageCustomerId === undefined) {
          Swal.fire({
            icon: 'warning',
            title: 'Update Sage Profile Required',
            text: 'Please update Sage Profile.',
          });
          return;
        }
        onReady();
      },
      () => {
        Swal.fire({
          icon: 'warning',
          title: 'Update Sage Profile Required',
          text: 'Please update Sage Profile.',
        });
      }
    );
  }

  /** Create uses portal → Sage POST v1/invoices/create; update uses POST v1/invoices/customers/{customerId}/transactions/{transactionId}/update. */
  private confirmSalesInvoiceFlow(mode: 'create' | 'update'): void {
    const inv = this.invoice;
    const startDateStr = this.invoice.startDate ? this.datepipe.transform(this.invoice.startDate, 'yyyy-MM-dd') : '';
    const isUpdate = mode === 'update';

    Swal.fire({
      icon: 'question',
      title: isUpdate ? 'Update Sales Invoice' : 'Create Sales Invoice',
      html: `
        <div class="text-start">
          <p class="mb-3">${isUpdate
            ? 'Do you want to update the sales invoice with the following details?'
            : 'Do you want to create a sales invoice with the following details?'}</p>
          <div class="mb-3">
            <strong>Resident Name:</strong> ${inv.billedTo || '-'}<br>
            <strong>Invoice ID:</strong> ${inv.id}<br>
            <strong>Invoice Number:</strong> ${inv.invoiceNumber || '-'}<br>
            <strong>Invoice Date:</strong> ${startDateStr}
          </div>
          <table class="table table-sm table-borderless mb-0">
            <tbody>
              <tr>
                <td>Taxable:</td>
                <td class="text-end">$${(inv.subTotalTaxableItems != null ? inv.subTotalTaxableItems : 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td>Non-Taxable:</td>
                <td class="text-end">$${(inv.subTotalNonTaxableItems != null ? inv.subTotalNonTaxableItems : 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td>HST:</td>
                <td class="text-end">$${(inv.hst != null ? inv.hst : 0).toFixed(2)}</td>
              </tr>
              <tr class="border-top">
                <td><strong>TOTAL:</strong></td>
                <td class="text-end"><strong>$${(inv.total != null ? inv.total : 0).toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: isUpdate ? 'Yes, update sales invoice' : 'Yes, create sales invoice',
    }).then(result => {
      if (!result.isConfirmed) return;

      this.showLoadingMessage = true;
      Swal.fire({
        title: isUpdate ? 'Updating sales invoice...' : 'Creating sales invoice...',
        text: 'Sage invoice and balance-forward sync can take 10–30 seconds. Please wait.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onOpen: () => { Swal.showLoading(); },
      });

      const request$ = isUpdate
        ? this.invoiceService.updateSalesInvoice(this.pharmacyId, this.facilityId, this.patientId, this.invoice.id)
        : this.invoiceService.createSalesInvoice(this.pharmacyId, this.facilityId, this.patientId, this.invoice.id);

      request$
        .pipe(
          timeout(90000),
          finalize(() => {
            Swal.close();
            this.showLoadingMessage = false;
          })
        )
        .subscribe(
          res => {
            if (!res.success) {
              this.loadErrorMessage(res.data);
              return;
            }
            this.sharedService.showSuccessMessage = true;
            setTimeout(() => { this.sharedService.showSuccessMessage = false; }, 5000);
            this.loadPatientInvoiceNavigationList();
            this.loadFields();
          },
          err => {
            var payload = (err && err.error) ? err.error : ((err && err.data) ? err.data : err);
            this.loadErrorMessage(payload);
          }
        );
    });
  }

  openPurchaseDialog(): void {
    if (this.disablePurchaseButton) {
      this.showPurchaseUnavailableSwal('Invoice is not available for payment purchase.');
      return;
    }
    this.openPaymentDialog();
  }

  openPaymentDialog(): void {
    if (!this.invoice) {
      return;
    }

    if (!this.hasPersistedInvoiceId()) {
      Swal.fire({
        icon: 'warning',
        title: 'Purchase',
        text: 'Invoice must be saved before purchase.',
        confirmButtonText: 'OK',
      });
      return;
    }

    const invoiceNumber = this.invoice.invoiceNumber != null ? String(this.invoice.invoiceNumber) : '-';
    const billedTo = this.invoice.billedTo != null ? String(this.invoice.billedTo) : '-';
    const invoiceId = String(this.invoice.id);
    const subTotalText = this.formatInvoiceCurrency(this.subTotal);
    const hstText = this.formatInvoiceCurrency(this.hst);
    const totalText = this.formatInvoiceCurrency(this.invoice.total);

    Swal.fire({
      icon: 'question',
      title: 'Purchase',
      html: ''
        + '<div style="max-width: 300px; margin: 0 auto; text-align: left;">'
        + '<table style="width: 100%; border-collapse: collapse; margin-bottom: 14px;">'
        + '<tr><td style="padding: 3px 12px 3px 0; white-space: nowrap; vertical-align: top;"><strong>Resident:</strong></td>'
        + '<td style="padding: 3px 0;">' + billedTo + '</td></tr>'
        + '<tr><td style="padding: 3px 12px 3px 0; white-space: nowrap;"><strong>InvoiceNumber:</strong></td>'
        + '<td style="padding: 3px 0;">' + invoiceNumber + '</td></tr>'
        + '<tr><td style="padding: 3px 12px 3px 0; white-space: nowrap;"><strong>InvoiceId:</strong></td>'
        + '<td style="padding: 3px 0;">' + invoiceId + '</td></tr>'
        + '</table>'
        + '<table style="width: 100%; border-collapse: collapse; margin-bottom: 14px;">'
        + '<tr><td style="padding: 4px 20px 4px 0;">SubTotal</td>'
        + '<td style="padding: 4px 0; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap;">' + subTotalText + '</td></tr>'
        + '<tr><td style="padding: 4px 20px 4px 0;">HST</td>'
        + '<td style="padding: 4px 0; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap;">' + hstText + '</td></tr>'
        + '<tr><td colspan="2" style="padding: 0; border-top: 1px solid #dee2e6;"></td></tr>'
        + '<tr><td style="padding: 8px 20px 4px 0;"><strong>Total</strong></td>'
        + '<td style="padding: 8px 0 4px 0; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap;"><strong>' + totalText + '</strong></td></tr>'
        + '</table>'
        + '<p class="text-muted mb-0" style="text-align: center;">Continue to open the payment purchase link.</p>'
        + '</div>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Continue',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then((result) => {
      if (!result || !result.isConfirmed) {
        return;
      }

      const purchaseWindow = this.openCenteredPopup(
        'about:blank',
        'solvoPaymentPurchase',
        520,
        850);
      if (!purchaseWindow) {
        this.loadErrorMessage('Please allow pop-ups to open the secure payment page.');
        return;
      }

      this.isCreatingPurchaseLink = true;
      this.invoiceService.generatePurchaseLink(this.invoice.id)
        .pipe(finalize(() => { this.isCreatingPurchaseLink = false; }))
        .subscribe(
          (response) => {
            if (!response || !response.url) {
              purchaseWindow.close();
              this.showPurchaseUnavailableSwal('Payment purchase link was not returned.');
              return;
            }
            purchaseWindow.location.href = response.url;
            this.watchPurchasePopupThenRefresh(purchaseWindow);
          },
          (err) => {
            purchaseWindow.close();
            const payload = (err && err.error) ? err.error : ((err && err.data) ? err.data : err);
            this.showPurchaseUnavailableSwal(payload);
          });
    });
  }

  private showPurchaseUnavailableSwal(payload: any): void {
    const self = this;
    Swal.fire({
      icon: 'error',
      title: 'Purchase',
      text: formatOopsErrorMessage(payload),
      confirmButtonText: 'OK'
    }).then(function (result) {
      if (result && result.isConfirmed) {
        self.loadFields();
      }
    });
  }

  private openCenteredPopup(url: string, name: string, width: number, height: number): Window {
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : (window as any).screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : (window as any).screenY;
    const screenWidth = window.innerWidth
      ? window.innerWidth
      : (document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width);
    const screenHeight = window.innerHeight
      ? window.innerHeight
      : (document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height);
    const left = Math.max(0, ((screenWidth - width) / 2) + dualScreenLeft);
    const top = Math.max(0, ((screenHeight - height) / 2) + dualScreenTop);
    const features = 'scrollbars=yes,resizable=yes,width=' + width
      + ',height=' + height
      + ',top=' + Math.round(top)
      + ',left=' + Math.round(left);
    const popup = window.open(url, name, features);
    if (popup && typeof popup.focus === 'function') {
      popup.focus();
    }
    return popup;
  }

  showPaymentReviewStatusChangeConfirm(status: number, forcePending: boolean = false): void {
    if (this.shouldBlockPaymentReviewStatusChange(status, forcePending)) {
      return;
    }

    const config = this.getPaymentReviewStatusChangeConfig(status, forcePending);
    const self = this;

    Swal.fire({
      icon: config.icon,
      title: config.title,
      html: config.html,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: config.confirmButtonText,
      confirmButtonColor: config.confirmButtonColor,
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then(function (result) {
      if (!result || !result.isConfirmed) {
        return;
      }
      self.savePaymentReviewStatus(status, forcePending);
    });
  }

  private shouldBlockPaymentReviewStatusChange(status: number, forcePending: boolean = false): boolean {
    if (status === REVIEW_STATUS.FLAGGED && this.disablePaymentFlagButton) {
      return true;
    }
    if (status === REVIEW_STATUS.APPROVED && this.disablePaymentApproveButton) {
      return true;
    }
    if (status === REVIEW_STATUS.PENDING) {
      if (forcePending) {
        return this.disablePaymentForcePendingButton;
      }
      return this.disablePaymentPendingButton;
    }
    return false;
  }

  private getPaymentReviewStatusChangeConfig(status: number, forcePending: boolean = false): any {
    const patientName = this.invoice && this.invoice.billedTo ? this.invoice.billedTo : 'this';
    if (status === REVIEW_STATUS.PENDING && forcePending) {
      return {
        icon: 'warning' as const,
        title: 'Force Payment Review to Pending',
        html: '<p>Force payment review to <strong>Pending</strong> on <strong>' + patientName
          + '</strong> invoice?</p><p class="text-muted small mb-0">Sets payment status to <strong>Unpaid</strong> and balances portal payment rows so purchase can run again after a Helcim refund. Use only for recovery (risk of double charge).</p>',
        confirmButtonText: 'Yes, force pending',
        confirmButtonColor: '#dc3545'
      };
    }
    const configs = {
      1: {
        icon: 'question' as const,
        title: 'Set Payment Review to Pending',
        html: '<p>Are you sure you want to set payment review to <strong>Pending</strong> on <strong>' + patientName + '</strong> invoice?</p>',
        confirmButtonText: 'Yes, set to pending',
        confirmButtonColor: '#ffc107'
      },
      2: {
        icon: 'warning' as const,
        title: 'Set Payment Review to Flagged',
        html: '<p>Are you sure you want to flag payment review on <strong>' + patientName + '</strong> invoice?</p>',
        confirmButtonText: 'Yes, flag it',
        confirmButtonColor: '#dc3545'
      },
      3: {
        icon: 'question' as const,
        title: 'Set Payment Review to Approved',
        html: '<p>Are you sure you want to approve payment review on <strong>' + patientName + '</strong> invoice?</p>',
        confirmButtonText: 'Yes, approve it',
        confirmButtonColor: '#28a745'
      }
    };
    return configs[status] || configs[1];
  }

  private savePaymentReviewStatus(status: number, forcePending: boolean = false): void {
    if (!this.invoice || !this.invoice.id) {
      return;
    }

    this.showLoadingMessage = true;
    this.invoiceService.updateInvoicePaymentReviewStatus(this.invoice.id, status, forcePending).subscribe(
      function (result) {
        this.showLoadingMessage = false;
        if (!result || !result.success || !result.data) {
          this.loadErrorMessage(result && result.data ? result.data : 'Unable to update payment review status.');
          return;
        }

        this.sharedService.showSuccessMessage = true;
        setTimeout(function () { this.sharedService.showSuccessMessage = false; }.bind(this), 3000);
        // Same as Refresh Page after Payment review actions.
        this.loadFields();
      }.bind(this),
      function (err) {
        this.showLoadingMessage = false;
        const payload = (err && err.error) ? err.error : err;
        this.loadErrorMessage(payload);
      }.bind(this)
    );
  }

  getPaymentStatusLabel(): string {
    if (this.invoice && this.invoice.paymentStatus) {
      return this.invoice.paymentStatus;
    }
    switch (this.getInvoicePaymentStatusId()) {
      case PAYMENT_STATUS.PARTIALLY_PAID:
        return 'PartiallyPaid';
      case PAYMENT_STATUS.PAID:
        return 'Paid';
      case PAYMENT_STATUS.PARTIALLY_REFUNDED:
        return 'PartiallyRefunded';
      case PAYMENT_STATUS.REFUNDED:
        return 'Refunded';
      default:
        return 'Unpaid';
    }
  }

  /** Invoice.InvoicePaymentStatusId (1 = Unpaid). Defaults to Unpaid when missing. */
  getInvoicePaymentStatusId(): number {
    if (!this.invoice) {
      return PAYMENT_STATUS.UNPAID;
    }
    const id = this.invoice.invoicePaymentStatusId;
    if (id != null && id > 0) {
      return id;
    }
    return PAYMENT_STATUS.UNPAID;
  }

  /** True when Sage receipt exists: Completed, Updated, or Manual (offline). */
  hasSagePaymentRecord(): boolean {
    if (this.invoice && this.invoice.isOldSagePayment === true) {
      return true;
    }
    const statusId = this.invoice && this.invoice.invoicePaymentSageStatusId != null
      ? Number(this.invoice.invoicePaymentSageStatusId)
      : 0;
    if (statusId === 3 || statusId === 4) {
      return true;
    }
    const label = this.invoice && this.invoice.paymentSageStatus
      ? String(this.invoice.paymentSageStatus).trim()
      : '';
    return label === 'Completed' || label === 'Updated' || label === 'Manual';
  }

  openPaymentReviewStatusDialog(status: number): void {
    this.showPaymentReviewStatusChangeConfirm(status);
  }

  openForceCompletePaymentDialog(): void {
    if (this.disableForceCompletePaymentButton) {
      return;
    }

    const invoiceNumber = this.invoice.invoiceNumber != null ? String(this.invoice.invoiceNumber) : '-';
    const invoiceId = String(this.invoice.id);
    const paymentIdText = this.forceCompletePaymentId != null ? String(this.forceCompletePaymentId) : '-';
    const amountText = this.forceCompleteAmount != null
      ? this.formatInvoiceCurrency(this.forceCompleteAmount)
      : '-';
    const self = this;

    Swal.fire({
      icon: 'warning',
      title: 'Force Create Payment',
      html: ''
        + '<p class="mb-2 text-start">Create the Portal payment record from the verified SolvoPayment purchase. '
        + 'If the Portal row already exists, it will be reused.</p>'
        + '<p class="mb-1 text-start"><strong>InvoiceNumber:</strong> ' + invoiceNumber + '</p>'
        + '<p class="mb-1 text-start"><strong>InvoiceId:</strong> ' + invoiceId + '</p>'
        + '<p class="mb-1 text-start"><strong>PaymentId:</strong> ' + paymentIdText + '</p>'
        + '<p class="mb-3 text-start"><strong>Amount:</strong> ' + amountText + '</p>'
        + '<p class="mb-0 text-start text-muted small">A PENDING purchase link is used when available; reuse does not require one.</p>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Force Create Payment',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then(function (result) {
      if (!result || !result.isConfirmed) {
        return;
      }
      self.forceCompletePurchase();
    });
  }

  openRunScheduledChargeDialog(): void {
    if (this.disableRunScheduledChargeButton) {
      return;
    }

    const invoiceNumber = this.invoice.invoiceNumber != null ? String(this.invoice.invoiceNumber) : '-';
    const invoiceId = String(this.invoice.id);
    const billedTo = this.invoice.billedTo != null ? String(this.invoice.billedTo) : '-';
    const self = this;

    Swal.fire({
      icon: 'warning',
      title: 'Run Scheduled Charge (QA)',
      html: ''
        + '<p class="mb-2 text-start">Runs the <strong>same scheduled-charge path</strong> as the Scheduler batch '
        + 'for this invoice only (MethodOrder card → bank). Sends the <strong>ACP Scheduled charges</strong> staff email.</p>'
        + '<p class="mb-1 text-start"><strong>Invoice #</strong> ' + invoiceNumber + '</p>'
        + '<p class="mb-1 text-start"><strong>Invoice Id</strong> ' + invoiceId + '</p>'
        + '<p class="mb-3 text-start"><strong>Resident</strong> ' + billedTo + '</p>'
        + '<p class="mb-0 text-start text-muted small">QA only. Eligible invoices: Payment Review Approved, net unpaid, no Sage BF receipt.</p>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Run Scheduled Charge',
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      focusCancel: true
    }).then(function (result) {
      if (!result || !result.isConfirmed) {
        return;
      }
      self.runScheduledCharge();
    });
  }

  openAdminRefundPaymentDialog(): void {
    if (this.disableAdminRefundPaymentButton) {
      return;
    }

    const invoiceNumber = this.invoice.invoiceNumber != null ? String(this.invoice.invoiceNumber) : '-';
    const invoiceId = String(this.invoice.id);
    const billedTo = this.invoice.billedTo != null ? String(this.invoice.billedTo) : '-';
    const paymentAmountText = this.invoice.paymentAmount != null
      ? this.formatInvoiceCurrency(this.invoice.paymentAmount)
      : '-';
    const refundAmountText = this.invoice.paymentRefundAmount != null
      ? this.formatInvoiceCurrency(this.invoice.paymentRefundAmount)
      : this.formatInvoiceCurrency(0);
    const self = this;

    Swal.fire({
      icon: 'warning',
      title: 'Refund Payment — permanent action',
      html: ''
        + '<p class="mb-2 text-start text-danger"><strong>Warning:</strong> This refunds the Helcim / SolvoPayment charge and updates Portal. '
        + 'It cannot be undone from SolvoPortal. Money may return to the customer card or bank account.</p>'
        + '<p class="mb-1 text-start"><strong>Invoice #</strong> ' + invoiceNumber + '</p>'
        + '<p class="mb-1 text-start"><strong>Invoice Id</strong> ' + invoiceId + '</p>'
        + '<p class="mb-1 text-start"><strong>Resident</strong> ' + billedTo + '</p>'
        + '<p class="mb-1 text-start"><strong>Paid</strong> ' + paymentAmountText + '</p>'
        + '<p class="mb-3 text-start"><strong>Already refunded</strong> ' + refundAmountText + '</p>'
        + '<p class="mb-0 text-start text-muted small">Full remaining balance will be refunded. If Sage 50 already has a Receive Payment, reverse it manually in Sage 50 Finance.</p>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, refund payment',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      focusCancel: true
    }).then(function (result) {
      if (!result || !result.isConfirmed) {
        return;
      }
      self.refundInvoicePayment();
    });
  }

  private refundInvoicePayment(): void {
    if (this.disableAdminRefundPaymentButton) {
      return;
    }

    const self = this;
    this.isRefundingPayment = true;
    this.showLoadingMessage = true;
    this.invoiceService.refundInvoicePayment(this.invoice.id)
      .pipe(finalize(function () {
        self.isRefundingPayment = false;
        self.showLoadingMessage = false;
      }))
      .subscribe(
        function () {
          Swal.fire({
            icon: 'success',
            title: 'Refund completed',
            text: 'Helcim / SolvoPayment and Portal payment records were updated.',
            confirmButtonText: 'OK'
          });
          self.reloadInvoiceAfterForceComplete();
        },
        function (err) {
          const detail = err && err.error && (err.error.message || err.error.detail)
            ? String(err.error.message || err.error.detail)
            : 'Refund could not be completed.';
          Swal.fire({
            icon: 'error',
            title: 'Refund failed',
            text: detail,
            confirmButtonText: 'OK'
          });
        });
  }

  private runScheduledCharge(): void {
    if (this.disableRunScheduledChargeButton) {
      return;
    }

    const self = this;
    this.isRunningScheduledCharge = true;
    this.showLoadingMessage = true;
    this.invoiceService.runScheduledCharge(this.invoice.id, true)
      .pipe(finalize(function () {
        self.isRunningScheduledCharge = false;
        self.showLoadingMessage = false;
      }))
      .subscribe(
        function (response) {
          const batch = response && response.data ? response.data : response;
          const items = batch && batch.items ? batch.items
            : (batch && batch.Items ? batch.Items : null);
          const first = items && items.length > 0 ? items[0] : null;
          const status = first
            ? String(first.status != null ? first.status : first.Status || '')
            : '';
          const detail = first
            ? String(first.detail != null ? first.detail : first.Detail || '')
            : '';
          const emailSent = !!(batch && (batch.emailSent === true || batch.EmailSent === true));
          const ok = status === 'Paid'
            || status === 'PendingSettlement'
            || status === 'LinkedExisting';
          Swal.fire({
            icon: ok ? 'success' : (status === 'Skipped' ? 'info' : 'warning'),
            title: ok ? 'Scheduled charge finished' : 'Scheduled charge result',
            html: ''
              + '<p class="mb-1 text-start"><strong>Status:</strong> ' + (status || '—') + '</p>'
              + '<p class="mb-1 text-start"><strong>Detail:</strong> ' + (detail || '—') + '</p>'
              + '<p class="mb-0 text-start"><strong>Email sent:</strong> ' + (emailSent ? 'Yes' : 'No') + '</p>',
            confirmButtonText: 'OK'
          });
          self.reloadInvoiceAfterForceComplete();
        },
        function (err) {
          const detail = err && err.error && (err.error.message || err.error.detail)
            ? String(err.error.message || err.error.detail)
            : 'Scheduled charge could not be completed.';
          Swal.fire({
            icon: 'error',
            title: 'Scheduled charge failed',
            text: detail,
            confirmButtonText: 'OK'
          });
        });
  }

  private refreshForceCompleteStatus(): void {
    this.forceCompleteEligible = false;
    this.forceCompletePaymentId = null;
    this.forceCompleteAmount = null;

    if (!this.hasInvoiceAdminPrivilege || !this.hasPersistedInvoiceId()) {
      return;
    }

    const self = this;
    this.forceCompleteStatusLoading = true;
    this.invoiceService.getForceCompletePurchaseStatus(this.invoice.id)
      .pipe(finalize(function () {
        self.forceCompleteStatusLoading = false;
      }))
      .subscribe(
        function (status) {
          if (!status || status.eligible !== true) {
            self.forceCompleteEligible = false;
            return;
          }
          self.forceCompleteEligible = true;
          self.forceCompletePaymentId = status.paymentId != null ? Number(status.paymentId) : null;
          self.forceCompleteAmount = status.amount != null ? Number(status.amount) : null;
        },
        function () {
          self.forceCompleteEligible = false;
        });
  }

  private forceCompletePurchase(): void {
    if (this.disableForceCompletePaymentButton) {
      return;
    }

    const self = this;
    this.isForceCompletingPurchase = true;
    this.showLoadingMessage = true;
    this.invoiceService.forceCompletePurchase(this.invoice.id)
      .pipe(finalize(function () {
        self.isForceCompletingPurchase = false;
        self.showLoadingMessage = false;
      }))
      .subscribe(
        function (response) {
          const data = response && response.data ? response.data : response;
          const reused = !!(data && (data.reused === true || data.Reused === true));
          if (reused) {
            Swal.fire({
              icon: 'success',
              title: 'Payment already linked',
              text: 'The Portal InvoicePayment row already existed and was reused.',
              confirmButtonColor: '#28a745'
            });
          } else {
            self.sharedService.showSuccessMessage = true;
            setTimeout(function () { self.sharedService.showSuccessMessage = false; }, 3000);
          }
          self.reloadInvoiceAfterForceComplete();
        },
        function (err) {
          const payload = (err && err.error) ? err.error : ((err && err.data) ? err.data : err);
          self.loadErrorMessage(payload);
          self.refreshForceCompleteStatus();
        });
  }

  /** Same as clicking Refresh Page after Admin / payment recovery actions. */
  private reloadInvoiceAfterForceComplete(): void {
    this.loadFields();
  }

  /** When the purchase popup closes, refresh detail like Refresh Page. */
  private watchPurchasePopupThenRefresh(popup: Window): void {
    if (!popup) {
      return;
    }
    const self = this;
    const timer = window.setInterval(function () {
      if (!popup.closed) {
        return;
      }
      window.clearInterval(timer);
      self.loadFields();
    }, 800);
  }

  private formatInvoiceCurrency(amount: any): string {
    const value = Number(amount);
    if (isNaN(value)) {
      return '$0.00';
    }
    return '$' + value.toFixed(2);
  }

  getDinColor(din: any): string {
    if (!din) return 'transparent';
    const dinStr = din.toString();

    const colors = [
      '#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF',
      '#E6B3FF', '#FFDAC1', '#E2F0CB', '#B5EAD7', '#C7CEEA',
      '#F497AD', '#D6AEFF', '#A2E1DB', '#FFD8B1', '#FFABAB',
      '#AFCBFF', '#FFCC99', '#D9A5B3', '#ACE5EE', '#CFDBC5'
    ];

    let hash = 0;
    for (let i = 0; i < dinStr.length; i++) {
      hash = dinStr.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }
}

// Highlight matching text in autocomplete options.
@Pipe({ name: 'highlight' })
export class HighlightPipe implements PipeTransform {
  transform(text: string, search): string {
    if (search && text && typeof search === 'string' && typeof text === 'string') {
      const pattern = search
        .replace(/[\-\[\]\/{}()*x+?.\\^$|]/g, '\\$&')
        .split(' ')
        .filter(t => t.length > 0)
        .join('|');
      const regex = new RegExp(pattern, 'gi');
      return search ? text.replace(regex, match => `<strong class="highlight">${match}</strong>`) : text;
    }
    return text;
  }
}
